/**
 * LoginScreen — username + password login.
 */
import { useState, type FC, type FormEvent } from 'react';
import { Icon } from './Icon';

interface Props {
  onLogin: (username: string, password: string) => Promise<boolean>;
}

async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export const LoginScreen: FC<Props> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;
    setStatus('loading');
    try {
      const passwordHash = await sha256(password);
      const ok = await onLogin(username.trim(), passwordHash);
      if (!ok) {
        setStatus('error');
        setErrorMsg('Неверные учётные данные');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Ошибка подключения к API');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-900">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black z-0" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[100px] animate-pulse" />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[100px] animate-pulse"
        style={{ animationDelay: '1s' }}
      />

      <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 p-10 rounded-3xl w-full max-w-md shadow-2xl relative z-10">
        <div className="text-center mb-10">
          <img src="/favicon/favicon.svg" className="w-16 h-16 object-contain mb-6 mx-auto" alt="Mockup Lab" />
          <h1 className="text-4xl font-bold text-white tracking-tight">Mockup Lab</h1>
          <p className="text-slate-400 mt-3 font-medium">Конструктор макетов</p>
        </div>

        {status === 'error' && (
          <div className="bg-red-500/10 text-red-300 p-4 rounded-xl mb-6 text-sm text-center border border-red-500/20 flex items-center justify-center gap-2">
            <Icon name="alert-circle" className="w-4 h-4" />
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              Логин
            </label>
            <input
              type="text"
              className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-5 py-4 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all placeholder-slate-600"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setStatus('idle');
              }}
              placeholder="admin"
              autoFocus
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
              Пароль
            </label>
            <input
              type="password"
              className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-xl px-5 py-4 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all placeholder-slate-600"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setStatus('idle');
              }}
              placeholder="Введите пароль"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-4 text-lg bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-200 transition-colors shadow-xl shadow-blue-900/20 mt-4 flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              'Вход...'
            ) : (
              <>
                Войти <Icon name="arrow-right" className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500">Restricted Access System</p>
        </div>
      </div>
    </div>
  );
};
